import { Play, Pause, Square, Mic, SkipBack, Headphones, Trash2, AlertTriangle, CheckCircle2, Undo2, Redo2, Timer, Scissors, Minus, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { LevelMeter } from './LevelMeter';
import { MAX_COMPENSATION_S } from '../../hooks/studio/studioAudioUtils';

const MAX_LATENCY_MS = Math.round(MAX_COMPENSATION_S * 1000);

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function TransportBar({ engine, onExportClick, canExport, onOpenCalibration }) {
  const {
    isPlaying, isRecording, position, duration, instrumental, vocal, micError,
    play, pause, stop, seek, startRecording, stopRecording,
    clearVocal,
    noiseCheck, runNoiseCheck, canUndo, undo, canRedo, redo,
    latencyMs, nudgeLatency,
    splitAt, armSplit, cancelSplit,
  } = engine;

  const splitArmed = splitAt != null;
  const isInsideInstrumental = instrumental
    && position > instrumental.offset + 0.25  // need at least 0.25s of audio on each side
    && position < instrumental.offset + instrumental.buffer.duration - 0.25;

  const hasAudio = instrumental || vocal;
  const isCheckingNoise = noiseCheck.status === 'checking';

  return (
    <div className="bg-surface border border-[#262626] rounded-xl p-4 space-y-3" data-testid="studio-transport">
      <div className="flex flex-wrap items-center gap-3">
        {/* Transport */}
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Button size="sm" variant="outline" className="border-[#333] h-9 w-9 p-0" onClick={() => seek(0)} disabled={isRecording} data-testid="transport-rewind">
            <SkipBack className="w-4 h-4" />
          </Button>
          {isPlaying && !isRecording ? (
            <Button size="sm" className="btn-primary h-9 w-9 p-0" onClick={pause} data-testid="transport-pause">
              <Pause className="w-4 h-4" />
            </Button>
          ) : (
            <Button size="sm" className="btn-primary h-9 w-9 p-0" onClick={() => play()} disabled={!hasAudio || isRecording} data-testid="transport-play">
              <Play className="w-4 h-4" />
            </Button>
          )}
          <Button size="sm" variant="outline" className="border-[#333] h-9 w-9 p-0" onClick={stop} disabled={isRecording} data-testid="transport-stop">
            <Square className="w-4 h-4" />
          </Button>
          {isRecording ? (
            <Button size="sm" onClick={stopRecording} className="bg-red-600 hover:bg-red-700 text-white h-9 animate-pulse" data-testid="transport-stop-record">
              <Square className="w-4 h-4 mr-1.5" />Stop Take
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => startRecording()}
              disabled={isCheckingNoise || noiseCheck.status === 'blocked'}
              title={noiseCheck.status === 'blocked' ? 'Background too loud — find a quiet space, then retest' : undefined}
              className="bg-red-600 hover:bg-red-700 text-white h-9"
              data-testid="transport-record"
            >
              <Mic className="w-4 h-4 mr-1.5" />{vocal ? 'Punch In' : 'Record'}
            </Button>
          )}
          {vocal && !isRecording && (
            <Button size="sm" variant="outline" className="border-[#333] h-9 text-xs text-red-400 hover:text-red-300" onClick={clearVocal} data-testid="transport-clear-vocal">
              <Trash2 className="w-3.5 h-3.5 mr-1" />Clear
            </Button>
          )}
          <Button
            size="sm" variant="outline" title="Undo"
            className="border-[#333] h-9 w-9 p-0"
            onClick={undo}
            disabled={!canUndo || isRecording}
            data-testid="transport-undo"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button
            size="sm" variant="outline" title="Redo"
            className="border-[#333] h-9 w-9 p-0"
            onClick={redo}
            disabled={!canRedo || isRecording}
            data-testid="transport-redo"
          >
            <Redo2 className="w-4 h-4" />
          </Button>
          {instrumental && (
            <Button
              size="sm"
              variant={splitArmed ? undefined : 'outline'}
              className={splitArmed ? 'btn-primary h-9 w-9 p-0' : 'border-[#333] h-9 w-9 p-0'}
              title="Cut the riddim at the playhead, then tap the side you want to erase"
              onClick={() => (splitArmed ? cancelSplit() : armSplit(position))}
              disabled={isRecording || (!splitArmed && !isInsideInstrumental)}
              data-testid="transport-split"
            >
              <Scissors className={`w-4 h-4 ${splitArmed ? '' : 'text-lime'}`} />
            </Button>
          )}
          <LevelMeter engine={engine} />
        </div>

        {/* Timer */}
        <div className="font-mono text-lime text-lg tabular-nums" data-testid="transport-timer">
          {fmt(position)} <span className="text-[#555] text-sm">/ {fmt(duration)}</span>
        </div>

        {/* Export — vocal FX + channel faders live on the timeline track headers */}
        <div className="flex flex-wrap items-center gap-4 ml-auto">
          <Button
            size="sm" onClick={onExportClick} disabled={!canExport || isRecording}
            title={canExport ? undefined : 'Load an instrumental or record a take to export.'}
            className="btn-primary h-9" data-testid="studio-export-btn"
          >
            Export Stems
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-[#777]">
          <Headphones className="w-3.5 h-3.5 text-lime shrink-0" />
          Wear headphones while recording — otherwise your mic will pick up the instrumental.
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Button
              size="sm" variant="outline"
              className="border-[#333] h-7 w-7 p-0 text-[#bbb] hover:text-white disabled:opacity-40"
              onClick={() => nudgeLatency(-10)}
              disabled={isRecording || latencyMs == null || latencyMs <= 0}
              title="Decrease latency by 10 ms — use if your vocal sounds early"
              data-testid="latency-nudge-down"
            >
              <Minus className="w-3 h-3" />
            </Button>
            <Button
              size="sm" variant="outline"
              className="border-[#333] h-7 text-[11px] text-[#bbb] hover:text-white"
              onClick={onOpenCalibration}
              disabled={isRecording}
              title="Measure your device's roundtrip latency so recorded vocals line up with the beat"
              data-testid="latency-cal-open-btn"
            >
              <Timer className="w-3.5 h-3.5 mr-1.5 text-lime" />
              {latencyMs != null ? (
                <>Latency <span className="ml-1 font-mono text-lime">{latencyMs} ms</span></>
              ) : (
                <>Calibrate Latency</>
              )}
            </Button>
            <Button
              size="sm" variant="outline"
              className="border-[#333] h-7 w-7 p-0 text-[#bbb] hover:text-white disabled:opacity-40"
              onClick={() => nudgeLatency(10)}
              disabled={isRecording || latencyMs == null || latencyMs >= MAX_LATENCY_MS}
              title="Increase latency by 10 ms — use if your vocal sounds late"
              data-testid="latency-nudge-up"
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
          <div className="hidden md:flex items-center gap-3 text-[10px] text-[#666]" data-testid="shortcut-hints">
            {[['Space', 'Play/Pause'], ['R', 'Record'], ['Ctrl+Z', 'Undo'], ['Ctrl+Shift+Z', 'Redo'], ['S', 'Stop'], ['0', 'Rewind']].map(([k, label]) => (
              <span key={k} className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-[#1a1a1a] border border-[#333] font-mono text-[#999]">{k}</kbd>
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
      {micError && <p className="text-xs text-red-400" data-testid="mic-error">{micError}</p>}

      {splitArmed && (
        <div className="flex flex-wrap items-center gap-2 text-xs bg-[#141414] border border-lime/40 rounded-lg px-3 py-2" data-testid="split-armed-msg">
          <Scissors className="w-4 h-4 text-lime shrink-0" />
          <span className="text-[#ddd]">
            Cut set at <span className="font-mono text-lime">{fmt(splitAt)}</span> — tap the side of the riddim you want to erase.
          </span>
          <Button size="sm" variant="outline" className="border-[#333] h-7 text-xs ml-auto" onClick={cancelSplit} data-testid="split-cancel-btn">
            Cancel
          </Button>
        </div>
      )}

      {/* Persistent room-noise verdict — measured automatically when the
          Studio opens, so Record rolls instantly with no gate. */}
      {isCheckingNoise && (
        <div className="flex flex-wrap items-center gap-2 text-xs bg-[#141414] border border-[#333] rounded-lg px-3 py-2" data-testid="noise-check-indicator">
          <span className="w-2 h-2 rounded-full bg-lime animate-pulse shrink-0" />
          <span className="text-[#bbb] font-mono tabular-nums">
            Checking background noise…{noiseCheck.db != null ? ` ${noiseCheck.db.toFixed(0)} dB` : ''}
          </span>
        </div>
      )}

      {noiseCheck.status === 'pending' && (
        <div className="flex flex-wrap items-center gap-2 text-xs bg-[#141414] border border-[#333] rounded-lg px-3 py-2" data-testid="noise-pending-msg">
          <Mic className="w-4 h-4 text-[#888] shrink-0" />
          <span className="text-[#bbb]">Mic check pending — tap anywhere to test your background noise.</span>
          <Button size="sm" variant="outline" className="border-[#333] h-7 text-xs ml-auto" onClick={runNoiseCheck} data-testid="noise-retest-btn">
            Test Now
          </Button>
        </div>
      )}

      {noiseCheck.status === 'quiet' && (
        <div className="flex flex-wrap items-center gap-2 text-xs bg-lime/10 border border-lime/40 rounded-lg px-3 py-2" data-testid="noise-quiet-msg">
          <CheckCircle2 className="w-4 h-4 text-lime shrink-0" />
          <span className="text-lime">
            Your background is quiet enough to record ({noiseCheck.db.toFixed(0)} dB).
          </span>
          <Button size="sm" variant="outline" className="border-[#333] h-7 text-xs ml-auto" onClick={runNoiseCheck} disabled={isRecording} data-testid="noise-retest-btn">
            Retest
          </Button>
        </div>
      )}

      {noiseCheck.status === 'warn' && (
        <div className="flex flex-wrap items-center gap-2 text-xs bg-amber-950/40 border border-amber-500/40 rounded-lg px-3 py-2" data-testid="noise-warn-msg">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-amber-300">
            Background noise is a bit high ({noiseCheck.db.toFixed(0)} dB). Your take may sound noisy.
          </span>
          <Button size="sm" variant="outline" className="border-[#333] h-7 text-xs ml-auto" onClick={runNoiseCheck} disabled={isRecording} data-testid="noise-retest-btn">
            Retest
          </Button>
        </div>
      )}

      {noiseCheck.status === 'blocked' && (
        <div className="flex flex-wrap items-center gap-2 text-xs bg-red-950/40 border border-red-500/40 rounded-lg px-3 py-2" data-testid="noise-blocked-msg">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-red-300">
            Your background is too loud to record ({noiseCheck.db.toFixed(0)} dB) — find a quiet space, then retest.
          </span>
          <Button size="sm" variant="outline" className="border-[#333] h-7 text-xs ml-auto" onClick={runNoiseCheck} data-testid="noise-retest-btn">
            Retest
          </Button>
        </div>
      )}
    </div>
  );
}
